import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone, Mail, ShieldCheck } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import QuoteDialog from "@/components/QuoteDialog";

const NAV = [
    { to: "/v2", label: "Home" },
    { to: "/about", label: "About Us" },
    {
        label: "Security Films",
        children: [
            { to: "/security-films/residential", label: "Residential" },
            { to: "/security-films/commercial", label: "Commercial" },
        ],
    },
    { to: "/security-cameras", label: "Security Cameras" },
    { to: "/blinds-shutters", label: "Blinds & Shutters" },
    { to: "/contact", label: "Contact Us" },
];

function TopBar() {
    return (
        <div className="bg-navy text-white text-sm" data-testid="v2-top-utility-bar">
            <div className="container-x grid grid-cols-1 md:grid-cols-3 items-center py-2.5 gap-2">
                <span className="hidden md:inline-flex items-center gap-2 text-white/80 font-medium tracking-wide justify-self-start">
                    <ShieldCheck className="h-3.5 w-3.5 text-gold" />
                    Proudly Canadian Owned and Operated
                </span>
                <a
                    href={`mailto:${BUSINESS.email}`}
                    className="hidden md:inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors justify-self-center"
                >
                    <Mail className="h-3.5 w-3.5 text-gold" />
                    {BUSINESS.email}
                </a>
                <a
                    data-testid="v2-topbar-phone-link"
                    href={BUSINESS.phoneHref}
                    className="inline-flex items-center gap-2 font-semibold tracking-wide hover:text-gold transition-colors justify-self-end"
                >
                    <Phone className="h-3.5 w-3.5 text-gold" />
                    {BUSINESS.phone}
                </a>
            </div>
        </div>
    );
}

function DesktopNav({ onOpenQuote }) {
    const [openDropdown, setOpenDropdown] = useState(false);
    return (
        <nav className="hidden lg:flex items-center gap-2" data-testid="v2-desktop-nav">
            {NAV.map((item) =>
                item.children ? (
                    <div
                        key={item.label}
                        className="relative"
                        onMouseEnter={() => setOpenDropdown(true)}
                        onMouseLeave={() => setOpenDropdown(false)}
                    >
                        <button className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-navy hover:text-[#C9A961] transition-colors">
                            {item.label}
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        {openDropdown && (
                            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 z-50">
                                <div className="card-premium w-60 p-2">
                                    {item.children.map((c) => (
                                        <NavLink
                                            key={c.to}
                                            to={c.to}
                                            className={({ isActive }) =>
                                                `block rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-[#FDFCFB] text-[#C9A961]"
                                                        : "text-navy hover:bg-[#FDFCFB] hover:text-[#C9A961]"
                                                }`
                                            }
                                        >
                                            {c.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/v2"}
                        className={({ isActive }) =>
                            `px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-[#C9A961]" : "text-navy hover:text-[#C9A961]"
                            }`
                        }
                    >
                        {item.label}
                    </NavLink>
                )
            )}
            <button
                data-testid="v2-header-quote-button"
                onClick={onOpenQuote}
                className="ml-3 btn-gold !py-2.5 !px-5 text-sm"
            >
                Get a Free Quote
            </button>
        </nav>
    );
}

function MobileNav({ open, onClose, onOpenQuote }) {
    const [expand, setExpand] = useState(false);
    const location = useLocation();
    React.useEffect(() => {
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    if (!open) return null;
    return (
        <div className="lg:hidden fixed inset-0 z-[60] bg-white">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <Link to="/v2" onClick={onClose}>
                    <img src={BUSINESS.logo} alt="GTA Secure Inc." className="h-14 w-auto max-w-[240px] object-contain" />
                </Link>
                <button onClick={onClose} className="p-2">
                    <X className="h-6 w-6 text-navy" />
                </button>
            </div>
            <div className="px-6 py-6 space-y-1">
                {NAV.map((item) =>
                    item.children ? (
                        <div key={item.label}>
                            <button
                                className="flex w-full items-center justify-between py-3 text-base font-semibold text-navy"
                                onClick={() => setExpand((v) => !v)}
                            >
                                {item.label}
                                <ChevronDown className={`h-4 w-4 transition-transform ${expand ? "rotate-180" : ""}`} />
                            </button>
                            {expand && (
                                <div className="pl-4 border-l-2 border-gold">
                                    {item.children.map((c) => (
                                        <NavLink key={c.to} to={c.to} onClick={onClose} className="block py-2.5 text-sm font-medium text-navy">
                                            {c.label}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <NavLink key={item.to} to={item.to} onClick={onClose} className="block py-3 text-base font-semibold text-navy">
                            {item.label}
                        </NavLink>
                    )
                )}
                <div className="pt-6">
                    <button onClick={() => { onClose(); onOpenQuote(); }} className="btn-gold w-full">
                        Get a Free Quote
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * V2 Header — emphasizes the logo by stacking it centrally above the navigation.
 */
export default function HeaderV2() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <TopBar />
            <header
                className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100"
                data-testid="v2-site-header"
            >
                {/* Centered, prominent logo row */}
                <div className="container-x flex items-center justify-between gap-4 py-3 lg:py-4">
                    <div className="w-32 lg:w-40" />
                    <Link
                        to="/v2"
                        className="flex-shrink-0"
                        data-testid="v2-site-logo-link"
                    >
                        <img
                            src={BUSINESS.logo}
                            alt="GTA Secure Inc."
                            className="h-20 md:h-24 lg:h-32 w-auto max-w-[540px] lg:max-w-[720px] object-contain"
                        />
                    </Link>
                    <div className="flex items-center gap-2 w-32 lg:w-40 justify-end">
                        <button
                            data-testid="v2-mobile-nav-open"
                            className="lg:hidden p-2"
                            onClick={() => setMobileOpen(true)}
                        >
                            <Menu className="h-6 w-6 text-navy" />
                        </button>
                    </div>
                </div>

                {/* Nav row below logo on desktop */}
                <div className="hidden lg:block border-t border-gray-100">
                    <div className="container-x flex items-center justify-center py-2">
                        <DesktopNav onOpenQuote={() => setQuoteOpen(true)} />
                    </div>
                </div>
            </header>
            <MobileNav
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                onOpenQuote={() => setQuoteOpen(true)}
            />
            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
        </>
    );
}
